import React, { useState, useEffect, useRef } from 'react';
import { User, Clock, X } from 'lucide-react';
import { GroupedTrade } from '../../../lib/utils/tradeUtils';
import { processTradeStep1, processTradeStep2, processDraftPickTrade, processCompleteTrade, cancelTradeOffer } from '../../../lib/api/tradeMessages';
import { sendTradeFinalizedEmail } from '../../../lib/api/emailNotifications';
import { TradeActions } from './TradeActions';
import { TradeStatus } from './TradeStatus';
import { PlayerTradeInfo } from './PlayerTradeInfo';
import { DraftPickTradeInfo } from './DraftPickTradeInfo';
import { getLeagueSettings } from '../../../lib/api/leagueSettings';
import { translate } from '../../../lib/translations';
import toast from 'react-hot-toast';
import { supabase, applyAndClearTradePlacements } from '../../../lib/supabase';
import TradePlacementModal, { PlayerPlacementItem } from '../../Trade/TradePlacementModal';
import { useThemeStore } from '../../../lib/themeStore';

interface GroupedTradeCardProps {
  trade: GroupedTrade;
  currentUserId: number;
  onStatusUpdate: (requestedPage?: number) => Promise<void> | void;
  language: 'en' | 'fr';
}

interface LeagueSettings {
  salary_cap: boolean;
  max_salary_cap: number;
  salary_cap_reserve: boolean;
  max_salary_cap_reserve: number;
  number_foward: number;
  number_def: number;
  number_goalie: number;
  number_reserve: number;
  number_scratch: number;
  tradevalidation: boolean;
}

interface SalaryCaps {
  mainClub: number;
  reserve: number;
}

interface RosterCounts {
  number_of_defenders: number;
  number_of_fowards: number;
  number_of_defenders_reserve: number;
  number_of_fowards_reserve: number;
}

interface GoalieCounts {
  number_of_goalie: number;
  number_of_goalie_reserve: number;
}

export function GroupedTradeCard({
  trade,
  currentUserId,
  onStatusUpdate,
  language,
}: GroupedTradeCardProps) {
  const [processing, setProcessing] = useState(false);
  const [leagueSettings, setLeagueSettings] = useState<LeagueSettings | null>(null);
  const [receiverSalaryCaps, setReceiverSalaryCaps] = useState<SalaryCaps>({ mainClub: 0, reserve: 0 });
  const [senderSalaryCaps, setSenderSalaryCaps] = useState<SalaryCaps>({ mainClub: 0, reserve: 0 });
  const [receiverRosterCounts, setReceiverRosterCounts] = useState<RosterCounts | null>(null);
  const [senderRosterCounts, setSenderRosterCounts] = useState<RosterCounts | null>(null);
  const [receiverGoalieCounts, setReceiverGoalieCounts] = useState<GoalieCounts | null>(null);
  const [senderGoalieCounts, setSenderGoalieCounts] = useState<GoalieCounts | null>(null);
  const [receiverScratchCount, setReceiverScratchCount] = useState<number>(0);
  const [senderScratchCount, setSenderScratchCount] = useState<number>(0);
  const [scratchStatuses, setScratchStatuses] = useState<{ [playerId: number]: boolean }>({});
  // Placement modal (destinataire choisit où placer les joueurs reçus)
  const [showPlacementModal, setShowPlacementModal] = useState(false);
  const [playersForPlacement, setPlayersForPlacement] = useState<PlayerPlacementItem[]>([]);
  const acceptancePlacementsRef = useRef<Record<string, 'main' | 'reserve'> | null>(null);
  const { theme } = useThemeStore();
  const isReceiver = currentUserId === trade.receiver_user_id;
  const isSender = currentUserId === trade.sender_user_id;
  const isPending = !trade.is_accepted && !trade.is_refused;

  useEffect(() => {
    async function loadSettings() {
      try {
        const { data, error } = await getLeagueSettings(trade.leagueid);
        if (error) throw error;
        setLeagueSettings(data);
      } catch (error) {
        console.error('Error loading league settings:', error);
      }
    }

    loadSettings();
  }, [trade.leagueid]);

  useEffect(() => {
    async function loadSalaryCaps() {
      try {
        // Load receiver salary caps
        const [receiverMainCap, receiverReserveCap] = await Promise.all([
          supabase
            .from('get_salary_cap_per_team')
            .select('salary_cap')
            .eq('league_id', trade.leagueid)
            .eq('user_id', trade.receiver_user_id)
            .eq('isnhl', true)
            .single(),
          supabase
            .from('get_salary_cap_per_team')
            .select('salary_cap')
            .eq('league_id', trade.leagueid)
            .eq('user_id', trade.receiver_user_id)
            .eq('isnhl', false)
            .single()
        ]);

        setReceiverSalaryCaps({
          mainClub: receiverMainCap.data?.salary_cap || 0,
          reserve: receiverReserveCap.data?.salary_cap || 0
        });

        // Load sender salary caps
        const [senderMainCap, senderReserveCap] = await Promise.all([
          supabase
            .from('get_salary_cap_per_team')
            .select('salary_cap')
            .eq('league_id', trade.leagueid)
            .eq('user_id', trade.sender_user_id)
            .eq('isnhl', true)
            .single(),
          supabase
            .from('get_salary_cap_per_team')
            .select('salary_cap')
            .eq('league_id', trade.leagueid)
            .eq('user_id', trade.sender_user_id)
            .eq('isnhl', false)
            .single()
        ]);

        setSenderSalaryCaps({
          mainClub: senderMainCap.data?.salary_cap || 0,
          reserve: senderReserveCap.data?.salary_cap || 0
        });
      } catch (error) {
        console.error('Error loading salary caps:', error);
      }
    }

    loadSalaryCaps();
  }, [trade.leagueid, trade.receiver_user_id, trade.sender_user_id]);

  useEffect(() => {
    async function loadRosterCounts() {
      try {
        // Load receiver roster counts
        const { data: receiverData, error: receiverError } = await supabase
          .from('get_number_type_player_per_team')
          .select('*')
          .eq('league_id', trade.leagueid)
          .eq('user_id', trade.receiver_user_id)
          .single();

        if (receiverError) throw receiverError;
        setReceiverRosterCounts(receiverData);

        // Load sender roster counts
        const { data: senderData, error: senderError } = await supabase
          .from('get_number_type_player_per_team')
          .select('*')
          .eq('league_id', trade.leagueid)
          .eq('user_id', trade.sender_user_id)
          .single();

        if (senderError) throw senderError;
        setSenderRosterCounts(senderData);

        // Load receiver goalie counts
        const { data: receiverGoalieData, error: receiverGoalieError } = await supabase
          .from('get_number_type_goalie_per_team')
          .select('*')
          .eq('league_id', trade.leagueid)
          .eq('user_id', trade.receiver_user_id)
          .single();

        if (receiverGoalieError) throw receiverGoalieError;
        setReceiverGoalieCounts(receiverGoalieData);

        // Load sender goalie counts
        const { data: senderGoalieData, error: senderGoalieError } = await supabase
          .from('get_number_type_goalie_per_team')
          .select('*')
          .eq('league_id', trade.leagueid)
          .eq('user_id', trade.sender_user_id)
          .single();

        if (senderGoalieError) throw senderGoalieError;
        setSenderGoalieCounts(senderGoalieData);

      } catch (error) {
        console.error('Error loading roster counts:', error);
      }
    }

    loadRosterCounts();
  }, [trade.leagueid, trade.receiver_user_id, trade.sender_user_id]);

  useEffect(() => {
    async function loadScratchCounts() {
      try {
        console.log('Loading scratch counts for trade validation');
        
        // Load receiver scratch count
        const { data: receiverScratchData, error: receiverScratchError } = await supabase
          .from('league_players_user')
          .select('player_id')
          .eq('league_id', trade.leagueid)
          .eq('user_id', trade.receiver_user_id)
          .eq('is_scratch', true);

        if (receiverScratchError) {
          console.error('Error loading receiver scratch count:', receiverScratchError);
        } else {
          const receiverCount = receiverScratchData?.length || 0;
          console.log(`Receiver (${trade.receiver_username}) current scratch players:`, receiverCount);
          setReceiverScratchCount(receiverCount);
        }

        // Load sender scratch count
        const { data: senderScratchData, error: senderScratchError } = await supabase
          .from('league_players_user')
          .select('player_id')
          .eq('league_id', trade.leagueid)
          .eq('user_id', trade.sender_user_id)
          .eq('is_scratch', true);

        if (senderScratchError) {
          console.error('Error loading sender scratch count:', senderScratchError);
        } else {
          const senderCount = senderScratchData?.length || 0;
          console.log(`Sender (${trade.sender_username}) current scratch players:`, senderCount);
          setSenderScratchCount(senderCount);
        }
      } catch (error) {
        console.error('Error loading scratch counts:', error);
      }
    }

    loadScratchCounts();
  }, [trade.leagueid, trade.receiver_user_id, trade.sender_user_id, trade.receiver_username, trade.sender_username]);

  useEffect(() => {
    async function loadScratchStatuses() {
      const statuses = await getPlayerScratchStatus();
      setScratchStatuses(statuses);
    }

    loadScratchStatuses();
  }, [trade.leagueid]);

  // Function to get scratch status for players in the trade
  const getPlayerScratchStatus = async () => {
    const scratchStatuses: { [playerId: number]: boolean } = {};
    
    try {
      console.log('Checking scratch status for players in trade');
      
      // Collect all player IDs from the trade
      const playerIds: number[] = [];
      trade.trades.forEach(tradePair => {
        if (tradePair.sender_player_id) playerIds.push(tradePair.sender_player_id);
        if (tradePair.sender_goalie_id) playerIds.push(tradePair.sender_goalie_id);
        if (tradePair.receiver_player_id) playerIds.push(tradePair.receiver_player_id);
        if (tradePair.receiver_goalie_id) playerIds.push(tradePair.receiver_goalie_id);
      });

      // Get scratch status for all players
      if (playerIds.length > 0) {
        const { data: scratchData, error: scratchError } = await supabase
          .from('league_players_user')
          .select('player_id, is_scratch')
          .eq('league_id', trade.leagueid)
          .in('player_id', playerIds);

        if (scratchError) {
          console.error('Error loading scratch statuses:', scratchError);
        } else {
          scratchData?.forEach(item => {
            scratchStatuses[item.player_id] = item.is_scratch || false;
            console.log(`Player ${item.player_id} scratch status:`, item.is_scratch);
          });
        }
      }
    } catch (error) {
      console.error('Error getting player scratch statuses:', error);
    }
    
    return scratchStatuses;
  };

  const calculateSalaryImpact = async () => {
    let receiverMainClubDelta = 0;
    let receiverReserveDelta = 0;
    let senderMainClubDelta = 0;
    let senderReserveDelta = 0;

    try {
      console.log('Calculating salary impact including scratch players adjustments');
      const scratchStatuses = await getPlayerScratchStatus();

      trade.trades.forEach(tradePair => {
        // Calculate receiver impact
        if (tradePair.sender_player_name && tradePair.sender_salary) {
          const isScratch = scratchStatuses[tradePair.sender_player_id!] || false;
          console.log(`Sender player ${tradePair.sender_player_name} - scratch: ${isScratch}, isnhl: ${tradePair.sender_isnhl}, salary: ${tradePair.sender_salary}`);
          
          // Scratch players always count on main club salary cap, even if isnhl = false
          if (isScratch || tradePair.sender_isnhl) {
            receiverMainClubDelta += tradePair.sender_salary;
          } else {
            receiverReserveDelta += tradePair.sender_salary;
          }
        }
        
        if (tradePair.sender_goalie_name && tradePair.sender_goalie_salary) {
          const isScratch = scratchStatuses[tradePair.sender_goalie_id!] || false;
          console.log(`Sender goalie ${tradePair.sender_goalie_name} - scratch: ${isScratch}, isnhl: ${tradePair.sender_isnhl}, salary: ${tradePair.sender_goalie_salary}`);
          
          // Scratch players always count on main club salary cap, even if isnhl = false
          if (isScratch || tradePair.sender_isnhl) {
            receiverMainClubDelta += tradePair.sender_goalie_salary;
          } else {
            receiverReserveDelta += tradePair.sender_goalie_salary;
          }
        }
        
        if (tradePair.receiver_player_name && tradePair.receiver_salary) {
          const isScratch = scratchStatuses[tradePair.receiver_player_id!] || false;
          console.log(`Receiver player ${tradePair.receiver_player_name} - scratch: ${isScratch}, isnhl: ${tradePair.receiver_isnhl}, salary: ${tradePair.receiver_salary}`);
          
          // Scratch players always count on main club salary cap, even if isnhl = false
          if (isScratch || tradePair.receiver_isnhl) {
            receiverMainClubDelta -= tradePair.receiver_salary;
          } else {
            receiverReserveDelta -= tradePair.receiver_salary;
          }
        }
        
        if (tradePair.receiver_goalie_name && tradePair.receiver_goalie_salary) {
          const isScratch = scratchStatuses[tradePair.receiver_goalie_id!] || false;
          console.log(`Receiver goalie ${tradePair.receiver_goalie_name} - scratch: ${isScratch}, isnhl: ${tradePair.receiver_isnhl}, salary: ${tradePair.receiver_goalie_salary}`);
          
          // Scratch players always count on main club salary cap, even if isnhl = false
          if (isScratch || tradePair.receiver_isnhl) {
            receiverMainClubDelta -= tradePair.receiver_goalie_salary;
          } else {
            receiverReserveDelta -= tradePair.receiver_goalie_salary;
          }
        }

        // Calculate sender impact (opposite of receiver)
        if (tradePair.sender_player_name && tradePair.sender_salary) {
          const isScratch = scratchStatuses[tradePair.sender_player_id!] || false;
          
          // Scratch players always count on main club salary cap, even if isnhl = false
          if (isScratch || tradePair.sender_isnhl) {
            senderMainClubDelta -= tradePair.sender_salary;
          } else {
            senderReserveDelta -= tradePair.sender_salary;
          }
        }
        
        if (tradePair.sender_goalie_name && tradePair.sender_goalie_salary) {
          const isScratch = scratchStatuses[tradePair.sender_goalie_id!] || false;
          
          // Scratch players always count on main club salary cap, even if isnhl = false
          if (isScratch || tradePair.sender_isnhl) {
            senderMainClubDelta -= tradePair.sender_goalie_salary;
          } else {
            senderReserveDelta -= tradePair.sender_goalie_salary;
          }
        }
        
        if (tradePair.receiver_player_name && tradePair.receiver_salary) {
          const isScratch = scratchStatuses[tradePair.receiver_player_id!] || false;
          
          // Scratch players always count on main club salary cap, even if isnhl = false
          if (isScratch || tradePair.receiver_isnhl) {
            senderMainClubDelta += tradePair.receiver_salary;
          } else {
            senderReserveDelta += tradePair.receiver_salary;
          }
        }
        
        if (tradePair.receiver_goalie_name && tradePair.receiver_goalie_salary) {
          const isScratch = scratchStatuses[tradePair.receiver_goalie_id!] || false;
          
          // Scratch players always count on main club salary cap, even if isnhl = false
          if (isScratch || tradePair.receiver_isnhl) {
            senderMainClubDelta += tradePair.receiver_goalie_salary;
          } else {
            senderReserveDelta += tradePair.receiver_goalie_salary;
          }
        }
      });

      console.log('Salary impact with scratch adjustments:', {
        receiver: { mainClubDelta: receiverMainClubDelta, reserveDelta: receiverReserveDelta },
        sender: { mainClubDelta: senderMainClubDelta, reserveDelta: senderReserveDelta }
      });

      return {
        receiver: { mainClubDelta: receiverMainClubDelta, reserveDelta: receiverReserveDelta },
        sender: { mainClubDelta: senderMainClubDelta, reserveDelta: senderReserveDelta }
      };
    } catch (error) {
      console.error('Error calculating salary impact:', error);
      // Return default values if error
      return {
        receiver: { mainClubDelta: 0, reserveDelta: 0 },
        sender: { mainClubDelta: 0, reserveDelta: 0 }
      };
    }
  };

  const calculateRosterImpact = async () => {
    let receiverMainForwardsDelta = 0;
    let receiverMainDefendersDelta = 0;
    let receiverMainGoaliesDelta = 0;
    let receiverReserveForwardsDelta = 0;
    let receiverReserveDefendersDelta = 0;
    let receiverReserveGoaliesDelta = 0;

    let senderMainForwardsDelta = 0;
    let senderMainDefendersDelta = 0;
    let senderMainGoaliesDelta = 0;
    let senderReserveForwardsDelta = 0;
    let senderReserveDefendersDelta = 0;
    let senderReserveGoaliesDelta = 0;

    try {
      console.log('=== VALIDATION EFFECTIF ÉCHANGE - DÉBUT ===');
      console.log('Calcul de l\'impact sur l\'effectif en excluant les joueurs scratch');
      const scratchStatuses = await getPlayerScratchStatus();

      // Récupérer les positions de tous les joueurs impliqués dans l'échange
      const playerPositions: { [playerId: number]: string } = {};
      
      for (const tradePair of trade.trades) {
        if (tradePair.sender_player_id) {
          const { data: senderPlayerData } = await supabase
            .from('getmyteamplayer')
            .select('positioncode')
            .eq('playerid', tradePair.sender_player_id)
            .eq('user_id', trade.sender_user_id)
            .eq('league_id', trade.leagueid)
            .single();
          
          if (senderPlayerData) {
            playerPositions[tradePair.sender_player_id] = senderPlayerData.positioncode;
          }
        }
        
        if (tradePair.receiver_player_id) {
          const { data: receiverPlayerData } = await supabase
            .from('getmyteamplayer')
            .select('positioncode')
            .eq('playerid', tradePair.receiver_player_id)
            .eq('user_id', trade.receiver_user_id)
            .eq('league_id', trade.leagueid)
            .single();
          
          if (receiverPlayerData) {
            playerPositions[tradePair.receiver_player_id] = receiverPlayerData.positioncode;
          }
        }
      }

      trade.trades.forEach(tradePair => {
        // Process sender's players going to receiver
        if (tradePair.sender_player_name) {
          const isScratch = scratchStatuses[tradePair.sender_player_id!] || false;
          const position = playerPositions[tradePair.sender_player_id!] || '';
          console.log(`Joueur sender ${tradePair.sender_player_name} - position: ${position}, scratch: ${isScratch}, isnhl: ${tradePair.sender_isnhl}`);
          
          // Les joueurs scratch ne comptent PAS dans les limites d'effectif du club principal
          if (!isScratch) {
            console.log(`  -> Joueur non-scratch, compte pour les limites d'effectif`);
            const isForward = ['L', 'C', 'R'].includes(position);
            const isDefender = position === 'D';
            
            if (tradePair.sender_isnhl) {
              if (isForward) {
                receiverMainForwardsDelta++;
                senderMainForwardsDelta--;
                console.log(`    -> Attaquant ${position} vers receiver club principal`);
              } else if (isDefender) {
                receiverMainDefendersDelta++;
                senderMainDefendersDelta--;
                console.log(`    -> Défenseur vers receiver club principal`);
              }
            } else {
              if (isForward) {
                receiverReserveForwardsDelta++;
                senderReserveForwardsDelta--;
                console.log(`    -> Attaquant ${position} vers receiver réserve`);
              } else if (isDefender) {
                receiverReserveDefendersDelta++;
                senderReserveDefendersDelta--;
                console.log(`    -> Défenseur vers receiver réserve`);
              }
            }
          } else {
            console.log(`  -> Joueur scratch, NE COMPTE PAS pour les limites d'effectif`);
          }
        }

        // Process sender's goalies going to receiver
        if (tradePair.sender_goalie_name) {
          const isScratch = scratchStatuses[tradePair.sender_goalie_id!] || false;
          console.log(`Gardien sender ${tradePair.sender_goalie_name} - scratch: ${isScratch}, isnhl: ${tradePair.sender_isnhl}`);
          
          // Les gardiens scratch ne comptent PAS dans les limites d'effectif du club principal
          if (!isScratch) {
            console.log(`  -> Gardien non-scratch, compte pour les limites d'effectif`);
            if (tradePair.sender_isnhl) {
              receiverMainGoaliesDelta++;
              senderMainGoaliesDelta--;
            } else {
              receiverReserveGoaliesDelta++;
              senderReserveGoaliesDelta--;
            }
          } else {
            console.log(`  -> Gardien scratch, NE COMPTE PAS pour les limites d'effectif`);
          }
        }

        // Process receiver's players going to sender
        if (tradePair.receiver_player_name) {
          const isScratch = scratchStatuses[tradePair.receiver_player_id!] || false;
          const position = playerPositions[tradePair.receiver_player_id!] || '';
          console.log(`Joueur receiver ${tradePair.receiver_player_name} - position: ${position}, scratch: ${isScratch}, isnhl: ${tradePair.receiver_isnhl}`);
          
          // Les joueurs scratch ne comptent PAS dans les limites d'effectif du club principal
          if (!isScratch) {
            console.log(`  -> Joueur non-scratch, compte pour les limites d'effectif`);
            const isForward = ['L', 'C', 'R'].includes(position);
            const isDefender = position === 'D';
            
            if (tradePair.receiver_isnhl) {
              if (isForward) {
                receiverMainForwardsDelta--;
                senderMainForwardsDelta++;
                console.log(`    -> Attaquant ${position} vers sender club principal`);
              } else if (isDefender) {
                receiverMainDefendersDelta--;
                senderMainDefendersDelta++;
                console.log(`    -> Défenseur vers sender club principal`);
              }
            } else {
              if (isForward) {
                receiverReserveForwardsDelta--;
                senderReserveForwardsDelta++;
                console.log(`    -> Attaquant ${position} vers sender réserve`);
              } else if (isDefender) {
                receiverReserveDefendersDelta--;
                senderReserveDefendersDelta++;
                console.log(`    -> Défenseur vers sender réserve`);
              }
            }
          } else {
            console.log(`  -> Joueur scratch, NE COMPTE PAS pour les limites d'effectif`);
          }
        }

        // Process receiver's goalies going to sender
        if (tradePair.receiver_goalie_name) {
          const isScratch = scratchStatuses[tradePair.receiver_goalie_id!] || false;
          console.log(`Gardien receiver ${tradePair.receiver_goalie_name} - scratch: ${isScratch}, isnhl: ${tradePair.receiver_isnhl}`);
          
          // Les gardiens scratch ne comptent PAS dans les limites d'effectif du club principal
          if (!isScratch) {
            console.log(`  -> Gardien non-scratch, compte pour les limites d'effectif`);
            if (tradePair.receiver_isnhl) {
              receiverMainGoaliesDelta--;
              senderMainGoaliesDelta++;
            } else {
              receiverReserveGoaliesDelta--;
              senderReserveGoaliesDelta++;
            }
          } else {
            console.log(`  -> Gardien scratch, NE COMPTE PAS pour les limites d'effectif`);
          }
        }
      });

      console.log('Roster impact (excluding scratch):', {
        receiver: {
          mainForwards: receiverMainForwardsDelta,
          mainDefenders: receiverMainDefendersDelta,
          mainGoalies: receiverMainGoaliesDelta,
          reserveForwards: receiverReserveForwardsDelta,
          reserveDefenders: receiverReserveDefendersDelta,
          reserveGoalies: receiverReserveGoaliesDelta
        },
        sender: {
          mainForwards: senderMainForwardsDelta,
          mainDefenders: senderMainDefendersDelta,
          mainGoalies: senderMainGoaliesDelta,
          reserveForwards: senderReserveForwardsDelta,
          reserveDefenders: senderReserveDefendersDelta,
          reserveGoalies: senderReserveGoaliesDelta
        }
      });

      return {
        receiver: {
          mainForwards: receiverMainForwardsDelta,
          mainDefenders: receiverMainDefendersDelta,
          mainGoalies: receiverMainGoaliesDelta,
          reserveForwards: receiverReserveForwardsDelta,
          reserveDefenders: receiverReserveDefendersDelta,
          reserveGoalies: receiverReserveGoaliesDelta
        },
        sender: {
          mainForwards: senderMainForwardsDelta,
          mainDefenders: senderMainDefendersDelta,
          mainGoalies: senderMainGoaliesDelta,
          reserveForwards: senderReserveForwardsDelta,
          reserveDefenders: senderReserveDefendersDelta,
          reserveGoalies: senderReserveGoaliesDelta
        }
      };
    } catch (error) {
      console.error('Error calculating roster impact:', error);
      // Return default values if error
      return {
        receiver: {
          mainForwards: 0,
          mainDefenders: 0,
          mainGoalies: 0,
          reserveForwards: 0,
          reserveDefenders: 0,
          reserveGoalies: 0
        },
        sender: {
          mainForwards: 0,
          mainDefenders: 0,
          mainGoalies: 0,
          reserveForwards: 0,
          reserveDefenders: 0,
          reserveGoalies: 0
        }
      };
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Function to calculate scratch players impact
  const calculateScratchImpact = async () => {
    let receiverScratchDelta = 0;
    let senderScratchDelta = 0;
    
    try {
      console.log('Calculating scratch players impact for trade');
      const scratchStatuses = await getPlayerScratchStatus();
      
      trade.trades.forEach(tradePair => {
        // Players going from sender to receiver
        if (tradePair.sender_player_id) {
          const isScratch = scratchStatuses[tradePair.sender_player_id] || false;
          if (isScratch) {
            console.log(`Sender player ${tradePair.sender_player_name} is scratch - moving to receiver`);
            receiverScratchDelta++;
            senderScratchDelta--;
          }
        }
        
        if (tradePair.sender_goalie_id) {
          const isScratch = scratchStatuses[tradePair.sender_goalie_id] || false;
          if (isScratch) {
            console.log(`Sender goalie ${tradePair.sender_goalie_name} is scratch - moving to receiver`);
            receiverScratchDelta++;
            senderScratchDelta--;
          }
        }
        
        // Players going from receiver to sender
        if (tradePair.receiver_player_id) {
          const isScratch = scratchStatuses[tradePair.receiver_player_id] || false;
          if (isScratch) {
            console.log(`Receiver player ${tradePair.receiver_player_name} is scratch - moving to sender`);
            receiverScratchDelta--;
            senderScratchDelta++;
          }
        }
        
        if (tradePair.receiver_goalie_id) {
          const isScratch = scratchStatuses[tradePair.receiver_goalie_id] || false;
          if (isScratch) {
            console.log(`Receiver goalie ${tradePair.receiver_goalie_name} is scratch - moving to sender`);
            receiverScratchDelta--;
            senderScratchDelta++;
          }
        }
      });
      
      console.log(`Scratch impact - Receiver delta: ${receiverScratchDelta}, Sender delta: ${senderScratchDelta}`);
      
      return {
        receiverScratchDelta,
        senderScratchDelta
      };
    } catch (error) {
      console.error('Error calculating scratch impact:', error);
      return { receiverScratchDelta: 0, senderScratchDelta: 0 };
    }
  };

  // Function to verify that all players in the trade still belong to their respective teams
  const verifyPlayersStillInTeams = async () => {
    for (const tradePair of trade.trades) {
      // Check sender's player (if exists)
      if (tradePair.sender_player_id) {
        const { data: senderPlayerData, error: senderPlayerError } = await supabase
          .from('league_players_user')
          .select('player_id')
          .eq('player_id', tradePair.sender_player_id)
          .eq('user_id', trade.sender_user_id)
          .eq('league_id', trade.leagueid)
          .single();

        if (senderPlayerError || !senderPlayerData) {
          throw new Error(`Le joueur ${tradePair.sender_player_name || 'inconnu'} ne fait plus partie de l'équipe de ${trade.sender_username}`);
        }
      }

      // Check sender's goalie (if exists)
      if (tradePair.sender_goalie_id) {
        const { data: senderGoalieData, error: senderGoalieError } = await supabase
          .from('league_players_user')
          .select('player_id')
          .eq('player_id', tradePair.sender_goalie_id)
          .eq('user_id', trade.sender_user_id)
          .eq('league_id', trade.leagueid)
          .single();

        if (senderGoalieError || !senderGoalieData) {
          throw new Error(`Le gardien ${tradePair.sender_goalie_name || 'inconnu'} ne fait plus partie de l'équipe de ${trade.sender_username}`);
        }
      }

      // Check receiver's player (if exists)
      if (tradePair.receiver_player_id) {
        const { data: receiverPlayerData, error: receiverPlayerError } = await supabase
          .from('league_players_user')
          .select('player_id')
          .eq('player_id', tradePair.receiver_player_id)
          .eq('user_id', trade.receiver_user_id)
          .eq('league_id', trade.leagueid)
          .single();

        if (receiverPlayerError || !receiverPlayerData) {
          throw new Error(`Le joueur ${tradePair.receiver_player_name || 'inconnu'} ne fait plus partie de l'équipe de ${trade.receiver_username}`);
        }
      }

      // Check receiver's goalie (if exists)
      if (tradePair.receiver_goalie_id) {
        const { data: receiverGoalieData, error: receiverGoalieError } = await supabase
          .from('league_players_user')
          .select('player_id')
          .eq('player_id', tradePair.receiver_goalie_id)
          .eq('user_id', trade.receiver_user_id)
          .eq('league_id', trade.leagueid)
          .single();

        if (receiverGoalieError || !receiverGoalieData) {
          throw new Error(`Le gardien ${tradePair.receiver_goalie_name || 'inconnu'} ne fait plus partie de l'équipe de ${trade.receiver_username}`);
        }
      }

      // Check sender's draft pick (if exists)
      if (tradePair.sender_pick_id) {
        const { data: senderPickData, error: senderPickError } = await supabase
          .from('draftpicks_user_league')
          .select('id')
          .eq('id', tradePair.sender_pick_id)
          .eq('user_id', trade.sender_user_id)
          .eq('league_id', trade.leagueid)
          .single();

        if (senderPickError || !senderPickData) {
          throw new Error(`Le choix de repêchage ${tradePair.sender_pick_description || 'inconnu'} n'appartient plus à ${trade.sender_username}`);
        }
      }

      // Check receiver's draft pick (if exists)
      if (tradePair.receiver_pick_id) {
        const { data: receiverPickData, error: receiverPickError } = await supabase
          .from('draftpicks_user_league')
          .select('id')
          .eq('id', tradePair.receiver_pick_id)
          .eq('user_id', trade.receiver_user_id)
          .eq('league_id', trade.leagueid)
          .single();

        if (receiverPickError || !receiverPickData) {
          throw new Error(`Le choix de repêchage ${tradePair.receiver_pick_description || 'inconnu'} n'appartient plus à ${trade.receiver_username}`);
        }
      }
    }
  };

  const handleCancelTradeOffer = async () => {
    if (processing) {
      toast.error(translate('processing', language));
      return;
    }

    setProcessing(true);

    try {
      const result = await cancelTradeOffer({
        thId: trade.th_id,
        leagueId: trade.leagueid,
      });

      if (result.error) throw result.error;

      toast.success(translate('trade.cancelled', language));
      // refresh current page
      onStatusUpdate(undefined);
    } catch (error: any) {
      console.error('Error cancelling trade offer:', error);
      toast.error(translate('failed.cancel.trade', language));
    } finally {
      setProcessing(false);
    }
  };

  const handleTradeResponse = async (accept: boolean) => {
    // Early return check with user feedback
    if (processing) {
      toast.error(translate('processing', language));
      return;
    }

    // Set processing state immediately for better UX
    setProcessing(true);

    try {

      // Process all trades in the group
      if (accept) {
        // First, verify that all players still belong to their respective teams
        try {
          await verifyPlayersStillInTeams();
        } catch (error: any) {
          toast.error(language === 'fr' 
            ? `Transaction refusée: ${error.message}`
            : `Trade rejected: ${error.message}`);
          return;
        }

        // Skip validations if tradevalidation is disabled
        if (leagueSettings?.tradevalidation === false) {
          console.log('=== VALIDATION DÉSACTIVÉE - ÉCHANGE DIRECT ===');
        } else {
          console.log('=== VALIDATION ACTIVÉE - VÉRIFICATION DES EXIGENCES ===');
          
          // Check salary cap implications only if settings and data are available
          if (leagueSettings && receiverSalaryCaps && senderSalaryCaps) {
            console.log('=== VALIDATION PLAFOND SALARIAL - DÉBUT ===');
            const { receiver, sender } = await calculateSalaryImpact();
          
          if (leagueSettings.salary_cap) {
            console.log('Vérification du plafond salarial du club principal activé');
            // Check receiver's main club salary cap
            const newReceiverMainClubSalary = receiverSalaryCaps.mainClub + receiver.mainClubDelta;
            console.log(`Receiver ${trade.receiver_username}: plafond actuel ${receiverSalaryCaps.mainClub}, delta ${receiver.mainClubDelta}, nouveau total ${newReceiverMainClubSalary}, limite ${leagueSettings.max_salary_cap}`);
            if (newReceiverMainClubSalary > leagueSettings.max_salary_cap) {
              const excess = newReceiverMainClubSalary - leagueSettings.max_salary_cap;
              toast.error(translate('error.receiver.salary.cap', language, {
                username: trade.receiver_username,
                excess: formatCurrency(excess)
              }));
              return;
            }

            // Check sender's main club salary cap
            const newSenderMainClubSalary = senderSalaryCaps.mainClub + sender.mainClubDelta;
            console.log(`Sender ${trade.sender_username}: plafond actuel ${senderSalaryCaps.mainClub}, delta ${sender.mainClubDelta}, nouveau total ${newSenderMainClubSalary}, limite ${leagueSettings.max_salary_cap}`);
            if (newSenderMainClubSalary > leagueSettings.max_salary_cap) {
              const excess = newSenderMainClubSalary - leagueSettings.max_salary_cap;
              toast.error(translate('error.sender.salary.cap', language, {
                username: trade.sender_username,
                excess: formatCurrency(excess)
              }));
              return;
            }
          }

          if (leagueSettings.salary_cap_reserve) {
            // Check receiver's reserve salary cap
            const newReceiverReserveSalary = receiverSalaryCaps.reserve + receiver.reserveDelta;
            if (newReceiverReserveSalary > leagueSettings.max_salary_cap_reserve) {
              const excess = newReceiverReserveSalary - leagueSettings.max_salary_cap_reserve;
              toast.error(translate('error.receiver.reserve.cap', language, {
                username: trade.receiver_username,
                excess: formatCurrency(excess)
              }));
              return;
            }

            // Check sender's reserve salary cap
            const newSenderReserveSalary = senderSalaryCaps.reserve + sender.reserveDelta;
            if (newSenderReserveSalary > leagueSettings.max_salary_cap_reserve) {
              const excess = newSenderReserveSalary - leagueSettings.max_salary_cap_reserve;
              toast.error(translate('error.sender.reserve.cap', language, {
                username: trade.sender_username,
                excess: formatCurrency(excess)
              }));
              return;
            }
          }
        }

        // Check scratch players limit
        if (leagueSettings && leagueSettings.number_scratch !== undefined) {
          console.log('=== VALIDATION JOUEURS SCRATCH - DÉBUT ===');
          console.log(`Limite de la ligue pour les joueurs scratch: ${leagueSettings.number_scratch}`);
          console.log(`Receiver ${trade.receiver_username} scratch actuels: ${receiverScratchCount}`);
          console.log(`Sender ${trade.sender_username} scratch actuels: ${senderScratchCount}`);
          
          const scratchImpact = await calculateScratchImpact();
          console.log(`Impact scratch calculé - Receiver delta: ${scratchImpact.receiverScratchDelta}, Sender delta: ${scratchImpact.senderScratchDelta}`);
          
          // Check receiver's scratch limit
          const newReceiverScratchCount = receiverScratchCount + scratchImpact.receiverScratchDelta;
          console.log(`Receiver nouveau total de scratch serait: ${newReceiverScratchCount} (limite: ${leagueSettings.number_scratch})`);
          
          if (newReceiverScratchCount > leagueSettings.number_scratch) {
            const excess = newReceiverScratchCount - leagueSettings.number_scratch;
            console.log(`❌ VALIDATION ÉCHOUÉE: Receiver dépasserait la limite de scratch par ${excess}`);
            toast.error(language === 'fr' 
              ? `Transaction refusée: ${trade.receiver_username} dépasserait la limite de joueurs scratch (${excess} de trop)`
              : `Trade rejected: ${trade.receiver_username} would exceed scratch players limit (${excess} over)`);
            return;
          }
          
          // Check sender's scratch limit
          const newSenderScratchCount = senderScratchCount + scratchImpact.senderScratchDelta;
          console.log(`Sender nouveau total de scratch serait: ${newSenderScratchCount} (limite: ${leagueSettings.number_scratch})`);
          
          if (newSenderScratchCount > leagueSettings.number_scratch) {
            const excess = newSenderScratchCount - leagueSettings.number_scratch;
            console.log(`❌ VALIDATION ÉCHOUÉE: Sender dépasserait la limite de scratch par ${excess}`);
            toast.error(language === 'fr' 
              ? `Transaction refusée: ${trade.sender_username} dépasserait la limite de joueurs scratch (${excess} de trop)`
              : `Trade rejected: ${trade.sender_username} would exceed scratch players limit (${excess} over)`);
            return;
          }
          
          console.log('✅ Validation des limites de scratch réussie');
        } else {
          console.log('⚠️  Aucune limite de joueurs scratch configurée pour cette ligue');
        }

        // Check roster limits only if all required data is available
        if (leagueSettings && receiverRosterCounts && senderRosterCounts && 
            receiverGoalieCounts && senderGoalieCounts) {
          console.log('=== VALIDATION LIMITES D\'EFFECTIF CLUB PRINCIPAL - DÉBUT ===');
          console.log('Validation des limites d\'effectif (excluant les joueurs scratch)');
          const rosterImpact = await calculateRosterImpact();

          // Check receiver's roster limits
          console.log(`Validation limites ${trade.receiver_username}:`);
          const newReceiverMainForwards = receiverRosterCounts.number_of_fowards + rosterImpact.receiver.mainForwards;
          console.log(`  Attaquants: ${receiverRosterCounts.number_of_fowards} + ${rosterImpact.receiver.mainForwards} = ${newReceiverMainForwards} (limite: ${leagueSettings.number_foward})`);
          if (newReceiverMainForwards > leagueSettings.number_foward) {
            console.log(`❌ VALIDATION ÉCHOUÉE: Receiver dépasserait la limite d'attaquants`);
            toast.error(translate('error.receiver.forward.limit', language, {
              username: trade.receiver_username,
              limit: leagueSettings.number_foward.toString()
            }));
            return;
          }

          const newReceiverMainDefenders = receiverRosterCounts.number_of_defenders + rosterImpact.receiver.mainDefenders;
          console.log(`  Défenseurs: ${receiverRosterCounts.number_of_defenders} + ${rosterImpact.receiver.mainDefenders} = ${newReceiverMainDefenders} (limite: ${leagueSettings.number_def})`);
          if (newReceiverMainDefenders > leagueSettings.number_def) {
            console.log(`❌ VALIDATION ÉCHOUÉE: Receiver dépasserait la limite de défenseurs`);
            toast.error(translate('error.receiver.defender.limit', language, {
              username: trade.receiver_username,
              limit: leagueSettings.number_def.toString()
            }));
            return;
          }

          const newReceiverMainGoalies = receiverGoalieCounts.number_of_goalie + rosterImpact.receiver.mainGoalies;
          console.log(`  Gardiens: ${receiverGoalieCounts.number_of_goalie} + ${rosterImpact.receiver.mainGoalies} = ${newReceiverMainGoalies} (limite: ${leagueSettings.number_goalie})`);
          if (newReceiverMainGoalies > leagueSettings.number_goalie) {
            console.log(`❌ VALIDATION ÉCHOUÉE: Receiver dépasserait la limite de gardiens`);
            toast.error(translate('error.receiver.goalie.limit', language, {
              username: trade.receiver_username,
              limit: leagueSettings.number_goalie.toString()
            }));
            return;
          }

          // Check sender's roster limits
          console.log(`Validation limites ${trade.sender_username}:`);
          const newSenderMainForwards = senderRosterCounts.number_of_fowards + rosterImpact.sender.mainForwards;
          console.log(`  Attaquants: ${senderRosterCounts.number_of_fowards} + ${rosterImpact.sender.mainForwards} = ${newSenderMainForwards} (limite: ${leagueSettings.number_foward})`);
          if (newSenderMainForwards > leagueSettings.number_foward) {
            console.log(`❌ VALIDATION ÉCHOUÉE: Sender dépasserait la limite d'attaquants`);
            toast.error(translate('error.sender.forward.limit', language, {
              username: trade.sender_username,
              limit: leagueSettings.number_foward.toString()
            }));
            return;
          }

          const newSenderMainDefenders = senderRosterCounts.number_of_defenders + rosterImpact.sender.mainDefenders;
          console.log(`  Défenseurs: ${senderRosterCounts.number_of_defenders} + ${rosterImpact.sender.mainDefenders} = ${newSenderMainDefenders} (limite: ${leagueSettings.number_def})`);
          if (newSenderMainDefenders > leagueSettings.number_def) {
            console.log(`❌ VALIDATION ÉCHOUÉE: Sender dépasserait la limite de défenseurs`);
            toast.error(translate('error.sender.defender.limit', language, {
              username: trade.sender_username,
              limit: leagueSettings.number_def.toString()
            }));
            return;
          }

          const newSenderMainGoalies = senderGoalieCounts.number_of_goalie + rosterImpact.sender.mainGoalies;
          console.log(`  Gardiens: ${senderGoalieCounts.number_of_goalie} + ${rosterImpact.sender.mainGoalies} = ${newSenderMainGoalies} (limite: ${leagueSettings.number_goalie})`);
          if (newSenderMainGoalies > leagueSettings.number_goalie) {
            console.log(`❌ VALIDATION ÉCHOUÉE: Sender dépasserait la limite de gardiens`);
            toast.error(translate('error.sender.goalie.limit', language, {
              username: trade.sender_username,
              limit: leagueSettings.number_goalie.toString()
            }));
            return;
          }

          console.log('✅ Validation des limites d\'effectif du club principal réussie');

          // Check reserve roster limits
          const receiverTotalReserve = (receiverRosterCounts.number_of_fowards_reserve + rosterImpact.receiver.reserveForwards) +
                                     (receiverRosterCounts.number_of_defenders_reserve + rosterImpact.receiver.reserveDefenders) +
                                     (receiverGoalieCounts.number_of_goalie_reserve + rosterImpact.receiver.reserveGoalies);
          if (receiverTotalReserve > leagueSettings.number_reserve) {
            toast.error(translate('error.receiver.reserve.limit', language, {
              username: trade.receiver_username,
              limit: leagueSettings.number_reserve.toString()
            }));
            return;
          }

          const senderTotalReserve = (senderRosterCounts.number_of_fowards_reserve + rosterImpact.sender.reserveForwards) +
                                   (senderRosterCounts.number_of_defenders_reserve + rosterImpact.sender.reserveDefenders) +
                                   (senderGoalieCounts.number_of_goalie_reserve + rosterImpact.sender.reserveGoalies);
          if (senderTotalReserve > leagueSettings.number_reserve) {
            toast.error(translate('error.sender.reserve.limit', language, {
              username: trade.sender_username,
              limit: leagueSettings.number_reserve.toString()
            }));
            return;
          }
      }      }
      }

      // Modal de placement: si le destinataire reçoit des joueurs, demander où les placer
      if (accept && isReceiver && acceptancePlacementsRef.current === null) {
        const playersToReceive: PlayerPlacementItem[] = trade.trades
          .flatMap(t => [
            t.sender_player_id && t.sender_player_name
              ? { id: String(t.sender_player_id), name: t.sender_player_name, salary: (t as any).sender_salary as number | undefined }
              : null,
            t.sender_goalie_id && t.sender_goalie_name
              ? { id: String(t.sender_goalie_id), name: t.sender_goalie_name, salary: (t as any).sender_goalie_salary as number | undefined }
              : null,
          ])
          .filter((p): p is PlayerPlacementItem => p !== null);

        if (playersToReceive.length > 0) {
          setPlayersForPlacement(playersToReceive);
          setShowPlacementModal(true);
          setProcessing(false); // Libérer pour l'interaction avec le modal
          return; // handleAcceptancePlacementConfirm reprendra l'exécution
        }
      }

      // Récupérer les placements confirmés par le modal (ou vide si aucun joueur à placer)
      const confirmedPlacements = acceptancePlacementsRef.current ?? {};
      acceptancePlacementsRef.current = null; // Nettoyer la référence

      const step1Result = await processTradeStep1({
        leagueId: trade.leagueid,
        thId: trade.th_id,
        isAccepted: accept,
        isRefused: !accept,
      });

      if (step1Result.error) throw step1Result.error;

      if (accept) {
        // Préparer les données pour la transaction atomique
        const tradeData = trade.trades.map(tradePair => ({
          sender_user_id: trade.sender_user_id,
          receiver_user_id: trade.receiver_user_id,
          sender_pick_id: tradePair.sender_pick_id,
          receiver_pick_id: tradePair.receiver_pick_id,
          sender_player_id: tradePair.sender_player_id,
          receiver_player_id: tradePair.receiver_player_id,
          sender_goalie_id: tradePair.sender_goalie_id,
          receiver_goalie_id: tradePair.receiver_goalie_id,
        }));

        console.log('=== DÉBOGAGE ÉCHANGE - DONNÉES ENVOYÉES ===');
        console.log('Trade data préparées pour processCompleteTrade:', JSON.stringify(tradeData, null, 2));
        console.log('League ID:', trade.leagueid);

        // Une seule transaction atomique pour TOUT (picks + joueurs + gardiens)
        const completeTradeResult = await processCompleteTrade({
          leagueId: trade.leagueid,
          trades: tradeData,
        });

        console.log('=== RÉSULTAT DE PROCESS_COMPLETE_TRADE ===');
        console.log('Résultat complet:', JSON.stringify(completeTradeResult, null, 2));

        if (completeTradeResult.error) {
          console.error('Erreur dans processCompleteTrade:', completeTradeResult.error);
          throw completeTradeResult.error;
        }

        // Appliquer les préférences de placement du destinataire (ses propres choix depuis le modal)
        if (Object.keys(confirmedPlacements).length > 0) {
          console.log('✅ Application des préférences de placement du destinataire...');
          for (const [playerId, placement] of Object.entries(confirmedPlacements)) {
            const { error: placementError } = await supabase
              .from('league_players_user')
              .update({ isnhl: placement === 'main', is_scratch: false })
              .eq('player_id', parseInt(playerId))
              .eq('league_id', trade.leagueid)
              .eq('user_id', trade.receiver_user_id);
            if (placementError) {
              console.warn(`Erreur placement joueur ${playerId}:`, placementError);
            }
          }
        }

        // Appliquer les préférences de placement du proposant (stockées lors de la proposition)
        try {
          const tradeGroupId = (trade as any).trades_id || trade.th_id;
          await applyAndClearTradePlacements(tradeGroupId, trade.leagueid);
          console.log('✅ Préférences de placement du proposant appliquées.');
        } catch (prefError) {
          console.warn('Erreur application préférences proposant:', prefError);
          // Non-bloquant
        }

        // VÉRIFICATION POST-TRANSFERT
        console.log('=== VÉRIFICATION POST-TRANSFERT ===');
        for (const tradePair of trade.trades) {
          // Vérifier les joueurs
          if (tradePair.sender_player_id) {
            const { data: playerCheck } = await supabase
              .from('league_players_user')
              .select('user_id, player_id')
              .eq('player_id', tradePair.sender_player_id)
              .eq('league_id', trade.leagueid)
              .single();
            console.log(`Joueur ${tradePair.sender_player_id} (${tradePair.sender_player_name}) appartient maintenant à user_id:`, playerCheck?.user_id, '(devrait être', trade.receiver_user_id, ')');
            
            if (playerCheck?.user_id !== trade.receiver_user_id) {
              console.error('❌ ÉCHEC: Le joueur n\'a pas été transféré correctement !');
            } else {
              console.log('✅ Joueur transféré correctement');
            }
          }

          if (tradePair.receiver_player_id) {
            const { data: playerCheck } = await supabase
              .from('league_players_user')
              .select('user_id, player_id')
              .eq('player_id', tradePair.receiver_player_id)
              .eq('league_id', trade.leagueid)
              .single();
            console.log(`Joueur ${tradePair.receiver_player_id} (${tradePair.receiver_player_name}) appartient maintenant à user_id:`, playerCheck?.user_id, '(devrait être', trade.sender_user_id, ')');
            
            if (playerCheck?.user_id !== trade.sender_user_id) {
              console.error('❌ ÉCHEC: Le joueur n\'a pas été transféré correctement !');
            } else {
              console.log('✅ Joueur transféré correctement');
            }
          }

          // Vérifier les gardiens
          if (tradePair.sender_goalie_id) {
            const { data: goalieCheck } = await supabase
              .from('league_players_user')
              .select('user_id, player_id')
              .eq('player_id', tradePair.sender_goalie_id)
              .eq('league_id', trade.leagueid)
              .single();
            console.log(`Gardien ${tradePair.sender_goalie_id} (${tradePair.sender_goalie_name}) appartient maintenant à user_id:`, goalieCheck?.user_id, '(devrait être', trade.receiver_user_id, ')');
            
            if (goalieCheck?.user_id !== trade.receiver_user_id) {
              console.error('❌ ÉCHEC: Le gardien n\'a pas été transféré correctement !');
            } else {
              console.log('✅ Gardien transféré correctement');
            }
          }

          if (tradePair.receiver_goalie_id) {
            const { data: goalieCheck } = await supabase
              .from('league_players_user')
              .select('user_id, player_id')
              .eq('player_id', tradePair.receiver_goalie_id)
              .eq('league_id', trade.leagueid)
              .single();
            console.log(`Gardien ${tradePair.receiver_goalie_id} (${tradePair.receiver_goalie_name}) appartient maintenant à user_id:`, goalieCheck?.user_id, '(devrait être', trade.sender_user_id, ')');
            
            if (goalieCheck?.user_id !== trade.sender_user_id) {
              console.error('❌ ÉCHEC: Le gardien n\'a pas été transféré correctement !');
            } else {
              console.log('✅ Gardien transféré correctement');
            }
          }

          // Vérifier les choix de draft
          if (tradePair.sender_pick_id) {
            const { data: pickCheck } = await supabase
              .from('draftpicks_user_league')
              .select('user_id, id')
              .eq('id', tradePair.sender_pick_id)
              .eq('league_id', trade.leagueid)
              .single();
            console.log(`Choix ${tradePair.sender_pick_id} (${tradePair.sender_pick_description}) appartient maintenant à user_id:`, pickCheck?.user_id, '(devrait être', trade.receiver_user_id, ')');
            
            if (pickCheck?.user_id !== trade.receiver_user_id) {
              console.error('❌ ÉCHEC: Le choix de draft n\'a pas été transféré correctement !');
            } else {
              console.log('✅ Choix de draft transféré correctement');
            }
          }

          if (tradePair.receiver_pick_id) {
            const { data: pickCheck } = await supabase
              .from('draftpicks_user_league')
              .select('user_id, id')
              .eq('id', tradePair.receiver_pick_id)
              .eq('league_id', trade.leagueid)
              .single();
            console.log(`Choix ${tradePair.receiver_pick_id} (${tradePair.receiver_pick_description}) appartient maintenant à user_id:`, pickCheck?.user_id, '(devrait être', trade.sender_user_id, ')');
            
            if (pickCheck?.user_id !== trade.sender_user_id) {
              console.error('❌ ÉCHEC: Le choix de draft n\'a pas été transféré correctement !');
            } else {
              console.log('✅ Choix de draft transféré correctement');
            }
          }
        }
      }

      // Emails de confirmation aux 2 partis — seulement si l'échange est accepté (non-bloquant)
      if (accept) {
        try {
          const [senderUserData, receiverUserData, leagueData] = await Promise.all([
            supabase.from('users').select('email').eq('user_id', trade.sender_user_id).maybeSingle(),
            supabase.from('users').select('email').eq('user_id', trade.receiver_user_id).maybeSingle(),
            supabase.from('leagues').select('name').eq('league_id', trade.leagueid).maybeSingle(),
          ]);
          const leagueName = leagueData.data?.name || '';
          const senderEmail = senderUserData.data?.email;
          const receiverEmail = receiverUserData.data?.email;

          const senderAssets = trade.trades.flatMap(t => [
            t.sender_player_name ? { name: t.sender_player_name, type: 'player' as const } : null,
            t.sender_goalie_name ? { name: t.sender_goalie_name, type: 'goalie' as const } : null,
            t.sender_pick_description ? { name: t.sender_pick_description, type: 'pick' as const } : null,
          ]).filter((a): a is { name: string; type: 'player' | 'goalie' | 'pick' } => a !== null);

          const receiverAssets = trade.trades.flatMap(t => [
            t.receiver_player_name ? { name: t.receiver_player_name, type: 'player' as const } : null,
            t.receiver_goalie_name ? { name: t.receiver_goalie_name, type: 'goalie' as const } : null,
            t.receiver_pick_description ? { name: t.receiver_pick_description, type: 'pick' as const } : null,
          ]).filter((a): a is { name: string; type: 'player' | 'goalie' | 'pick' } => a !== null);

          if (senderEmail) {
            await sendTradeFinalizedEmail({
              userEmail: senderEmail,
              username: trade.sender_username,
              otherUsername: trade.receiver_username,
              leagueName,
              assetsReceived: receiverAssets,
              assetsSent: senderAssets,
            });
          }
          if (receiverEmail) {
            await sendTradeFinalizedEmail({
              userEmail: receiverEmail,
              username: trade.receiver_username,
              otherUsername: trade.sender_username,
              leagueName,
              assetsReceived: senderAssets,
              assetsSent: receiverAssets,
            });
          }
        } catch (emailErr) {
          console.error('Trade finalized emails failed (non-blocking):', emailErr);
        }
      }

      toast.success(accept ? translate('trade.accepted', language) : translate('trade.rejected', language));
      // refresh current page (undefined -> parent keeps current page)
      onStatusUpdate(undefined);
    } catch (error: any) {
      console.error('=== ERREUR LORS DU TRAITEMENT DE L\'ÉCHANGE ===');
      console.error('Erreur complète:', error);
      console.error('Message d\'erreur:', error.message);
      console.error('Stack trace:', error.stack);
      if (error.details) {
        console.error('Détails de l\'erreur:', error.details);
      }
      if (error.hint) {
        console.error('Indice:', error.hint);
      }
      toast.error(translate('failed.process.trade', language));
    } finally {
      setProcessing(false);
    }
  };

  // Appelé quand le destinataire confirme le placement des joueurs reçus dans le modal
  const handleAcceptancePlacementConfirm = (placements: Record<string, 'main' | 'reserve'>) => {
    setShowPlacementModal(false);
    setPlayersForPlacement([]);
    acceptancePlacementsRef.current = placements;
    // Reprendre l'exécution avec les placements confirmés (les validations seront re-vérifiées)
    handleTradeResponse(true);
  };

  const handleAcceptancePlacementCancel = () => {
    setShowPlacementModal(false);
    setPlayersForPlacement([]);
    acceptancePlacementsRef.current = null;
  };

  return (
    <>
    <div className="card border shadow-sm mb-3">
      <div className="card-header bg-light d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <User size={18} className="me-2 text-primary" />
          <span className="fw-bold">
            {isReceiver ? trade.sender_username : trade.receiver_username}
          </span>
          {/* Indicateur de validation */}
          {leagueSettings && (
            <span 
              className={`badge ms-2 ${
                leagueSettings.tradevalidation === false 
                  ? 'bg-success' 
                  : 'bg-warning text-dark'
              }`}
              title={
                leagueSettings.tradevalidation === false
                  ? language === 'fr' 
                    ? 'Validation désactivée - Échange direct'
                    : 'Validation disabled - Direct trade'
                  : language === 'fr'
                    ? 'Validation activée - Vérification des exigences'
                    : 'Validation enabled - Requirements check'
              }
            >
              {leagueSettings.tradevalidation === false 
                ? (language === 'fr' ? 'DIRECT' : 'DIRECT')
                : (language === 'fr' ? 'VALIDATION' : 'VALIDATION')
              }
            </span>
          )}
        </div>
        <small className="text-muted">
          {new Date(trade.time_stamp).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
        </small>
      </div>
      <div className="card-body">
        <div className="row">
          <div className="col-md-6">
            <h6 className="mb-3">{translate('offered.assets', language)}:</h6>
            <div className="list-group">
              {trade.trades.map((tradePair, index) => (
                <React.Fragment key={`sender-${index}`}>
                  {tradePair.sender_player_name && (
                    <PlayerTradeInfo
                      name={tradePair.sender_player_name}
                      goals={tradePair.sender_goals}
                      assists={tradePair.sender_assists}
                      points={tradePair.sender_points}
                      salary={tradePair.sender_salary}
                      yearleft={tradePair.sender_yearleft}
                      isNHL={tradePair.sender_isnhl}
                      isScratch={scratchStatuses[tradePair.sender_player_id!] || false}
                      salaryRetainPct={tradePair.sender_salary_retain_pct}
                      language={language}
                    />
                  )}
                  {tradePair.sender_goalie_name && (
                    <PlayerTradeInfo
                      isGoalie
                      name={tradePair.sender_goalie_name}
                      wins={tradePair.sender_goalie_wins}
                      savepct={tradePair.sender_goalie_savepct}
                      shutouts={tradePair.sender_goalie_shutout}
                      salary={tradePair.sender_goalie_salary}
                      yearleft={tradePair.sender_goalie_yearleft}
                      isNHL={tradePair.sender_isnhl}
                      isScratch={scratchStatuses[tradePair.sender_goalie_id!] || false}
                      salaryRetainPct={tradePair.sender_salary_retain_pct}
                      language={language}
                    />
                  )}
                  {tradePair.sender_pick_description && (
                    <DraftPickTradeInfo
                      description={tradePair.sender_pick_description}
                      originalUsername={tradePair.sender_pick_original_username}
                      language={language}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="col-md-6">
            <h6 className="mb-3">{translate('requested.assets', language)}:</h6>
            <div className="list-group">
              {trade.trades.map((tradePair, index) => (
                <React.Fragment key={`receiver-${index}`}>
                  {tradePair.receiver_player_name && (
                    <PlayerTradeInfo
                      name={tradePair.receiver_player_name}
                      goals={tradePair.receiver_goals}
                      assists={tradePair.receiver_assists}
                      points={tradePair.receiver_points}
                      salary={tradePair.receiver_salary}
                      yearleft={tradePair.receiver_yearleft}
                      isNHL={tradePair.receiver_isnhl}
                      isScratch={scratchStatuses[tradePair.receiver_player_id!] || false}
                      salaryRetainPct={tradePair.receiver_salary_retain_pct}
                      language={language}
                    />
                  )}
                  {tradePair.receiver_goalie_name && (
                    <PlayerTradeInfo
                      isGoalie
                      name={tradePair.receiver_goalie_name}
                      wins={tradePair.receiver_goalie_wins}
                      savepct={tradePair.receiver_goalie_savepct}
                      shutouts={tradePair.receiver_goalie_shutout}
                      salary={tradePair.receiver_goalie_salary}
                      yearleft={tradePair.receiver_goalie_yearleft}
                      isNHL={tradePair.receiver_isnhl}
                      isScratch={scratchStatuses[tradePair.receiver_goalie_id!] || false}
                      salaryRetainPct={tradePair.receiver_salary_retain_pct}
                      language={language}
                    />
                  )}
                  {tradePair.receiver_pick_description && (
                    <DraftPickTradeInfo
                      description={tradePair.receiver_pick_description}
                      originalUsername={tradePair.receiver_pick_original_username}
                      language={language}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {isPending && isReceiver && leagueSettings && (
          <div className={`alert ${
            leagueSettings.tradevalidation === false 
              ? 'alert-success' 
              : 'alert-warning'
          } mt-3 mb-3`}>
            <div className="d-flex align-items-center">
              <div className="me-2">
                {leagueSettings.tradevalidation === false ? '✅' : '⚠️'}
              </div>
              <div>
                <strong>
                  {leagueSettings.tradevalidation === false 
                    ? (language === 'fr' ? 'VALIDATION DÉSACTIVÉE - ÉCHANGE DIRECT' : 'VALIDATION DISABLED - DIRECT TRADE')
                    : (language === 'fr' ? 'VALIDATION ACTIVÉE - VÉRIFICATION DES EXIGENCES' : 'VALIDATION ENABLED - REQUIREMENTS CHECK')
                  }
                </strong>
                <div className="small mt-1">
                  {leagueSettings.tradevalidation === false 
                    ? (language === 'fr' 
                        ? 'Cet échange sera accepté directement sans vérifier les plafonds salariaux ou les limites de roster.'
                        : 'This trade will be accepted directly without checking salary caps or roster limits.'
                      )
                    : (language === 'fr'
                        ? 'Cet échange sera vérifié pour respecter les plafonds salariaux et les limites de roster avant d\'être accepté.'
                        : 'This trade will be validated for salary caps and roster limits before being accepted.'
                      )
                  }
                </div>
              </div>
            </div>
          </div>
        )}

        {isPending && isReceiver && (
          <TradeActions
            onAccept={() => handleTradeResponse(true)}
            onReject={() => handleTradeResponse(false)}
            processing={processing}
            language={language}
          />
        )}

        {isPending && isSender && leagueSettings && (
          <div className={`alert ${
            leagueSettings.tradevalidation === false 
              ? 'alert-success' 
              : 'alert-info'
          } mt-3 mb-3`}>
            <div className="d-flex align-items-center">
              <div className="me-2">
                {leagueSettings.tradevalidation === false ? '✅' : 'ℹ️'}
              </div>
              <div className="small">
                <strong>Mode de validation:</strong>{' '}
                {leagueSettings.tradevalidation === false 
                  ? (language === 'fr' ? 'Désactivé (Échange direct)' : 'Disabled (Direct trade)')
                  : (language === 'fr' ? 'Activé (Vérification des exigences)' : 'Enabled (Requirements check)')
                }
              </div>
            </div>
          </div>
        )}

        {isPending && isSender && (
          <div className="mt-4">
            <div className="d-flex align-items-center justify-content-center text-warning mb-3">
              <Clock size={18} className="me-2" />
              <span>{translate('waiting.response', language, { username: trade.receiver_username })}</span>
            </div>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-outline-danger btn-sm d-flex align-items-center"
                onClick={handleCancelTradeOffer}
                disabled={processing}
              >
                <X size={16} className="me-1" />
                {translate('cancel.trade', language)}
              </button>
            </div>
          </div>
        )}

        {!isPending && <TradeStatus isAccepted={trade.is_accepted} language={language} />}
      </div>
    </div>

    {/* Modal de placement des joueurs reçus — destiné au destinataire de l'échange */}
    <TradePlacementModal
      show={showPlacementModal}
      players={playersForPlacement}
      language={language}
      theme={theme}
      isProposer={false}
      onConfirm={handleAcceptancePlacementConfirm}
      onCancel={handleAcceptancePlacementCancel}
    />
    </>
  );
}